import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CharityResponse, Charity, ApiCharityResponse } from '../../models/charity/charity.model';
import { RestfulApiService } from '../restful-api.service';
import { MOCK_CHARITY_DATA, MOCK_CHARITY_LONG_NAME } from '../../mocks/charity-mock.data';

// Re-export types for convenience
export type { CharityResponse, Charity };

@Injectable({
  providedIn: 'root',
})
export class CharityService {
  // Set to true to use mock data for development/testing
  // When enabled, uses MOCK_CHARITY_LONG_NAME (300-char name) to test:
  // - Multi-line name wrapping
  // - Conditional website/location icon hiding (empty values)
  private useMockData = false;

  constructor(private api: RestfulApiService) {}

  /**
   * Enables mock data mode for development/testing
   * Call this method to use MOCK_CHARITY_LONG_NAME instead of API calls
   * The mock includes a 300-character name to test multi-line display
   */
  enableMockData(): void {
    this.useMockData = true;
  }

  /**
   * Disables mock data mode and returns to using real API calls
   */
  disableMockData(): void {
    this.useMockData = false;
  }

  /**
   * Get charity by UUID and language
   * @param uuid - Charity UUID
   * @param language - Language code (e.g., 'eng', 'spa', 'fra')
   * @param preview - Preview mode flag (default: false)
   * @param globalSearch - Global search flag (default: false)
   * @returns Observable<CharityResponse>
   */
  getCharity(
    uuid: string,
    language: string = 'eng',
    preview: boolean = false,
    globalSearch: boolean = false
  ): Observable<CharityResponse> {
    // Return mock data if enabled
    if (this.useMockData) {
      // Use long name mock to test multi-line name display and conditional icon hiding
      return of({
        success: true,
        data: MOCK_CHARITY_LONG_NAME,
        message: 'Mock charity data loaded (with 300-character name for testing)'
      });
    }

    const params = {
      languageCode: language,
      preview: preview.toString(),
      globalSearch: globalSearch.toString(),
    };

    return this.api.get(
      `/v1/organisation/details/${uuid}`,
      params
    ).pipe(
      map((apiResponse: any) => this.mapApiResponseToCharity(apiResponse))
    );
  }

  /**
   * Get charity by UUID (without language parameter)
   * @param uuid - Charity UUID
   * @returns Observable<CharityResponse>
   */
  getCharityByUuid(uuid: string): Observable<CharityResponse> {
    return this.getCharity(uuid, 'eng');
  }

  /**
   * Get charity preview by UUID (simplified response with fewer fields)
   * @param uuid - Charity UUID
   * @param language - Language code (optional, e.g., 'eng', 'spa', 'fra')
   * @returns Observable<CharityResponse>
   */
  getCharityPreview(
    uuid: string,
    language?: string
  ): Observable<CharityResponse> {
    const params: any = {};
    if (language) {
      params.languageCode = language;
    }

    return this.api.get(
      `/v1/organisation/preview/${uuid}`,
      params
    ).pipe(
      map((apiResponse: any) => this.mapApiResponseToCharity(apiResponse))
    );
  }

  /**
   * Maps the API response to the Charity model format
   * @param apiResponse - The raw API response
   * @returns CharityResponse - The mapped response
   */
  private mapApiResponseToCharity(apiResponse: ApiCharityResponse): CharityResponse {
    const data = apiResponse.response;

    const charity: Charity = {
      id: data.organisationUuid,
      uuid: data.organisationUuid,
      name: data.organisationName || '',
      status: data.organisationLanguages?.find((lang: any) => lang.primaryLanguage)?.entityStatus,
      tagline: '',
      description: data.executiveSummary || '',
      address: data.address ? {
        street: data.address.streetName || '',
        streetNumber: data.address.streetNumber || '',
        city: data.address.city || '',
        state: data.address.stateProvince || '',
        postalCode: data.address.postalCode || '',
        country: data.address.country || '',
        landmarkAddress: data.address.landmarkAddress || '',
      } : undefined,
      coverImage: data.organisationCoverImageDto ? {
        id: data.organisationCoverImageDto.uuid,
        url: data.organisationCoverImageDto.url,
        alt: data.organisationName || '',
      } : undefined,
      logo: data.organisationLogoDto ? {
        id: data.organisationLogoDto.uuid,
        url: data.organisationLogoDto.url,
        alt: data.organisationName || '',
        type: 'logo',
      } : undefined,
      badge: data.badges && data.badges.length > 0 && data.badges[0].image ? {
        id: data.badges[0].image.uuid || data.badges[0].uuid || '',
        url: data.badges[0].image.url || '',
        alt: data.badges[0].name || 'Badge',
      } : undefined, // Deprecated: kept for backwards compatibility
      badges: data.badges && Array.isArray(data.badges) ? data.badges
        .filter((badge: any) => badge.image && badge.image.url) // Only include badges with valid images
        .map((badge: any) => ({
          id: badge.image.uuid || badge.uuid || '',
          url: badge.image.url,
          alt: badge.name || 'Badge',
        })) : undefined,
      images: data.organisationImagesDto?.map((img: any) => ({
        id: img.uuid,
        url: img.url,
        alt: data.organisationName || '',
        type: 'image' as const,
        isPrimary: img.position === 1,
        position: img.position,
      })) || [],
      videos: data.organisationVideoDto ? [{
        id: data.organisationVideoDto.uuid,
        url: data.organisationVideoDto.url,
        alt: data.organisationName || '',
        type: 'video' as const,
        thumbnail: data.organisationCoverImageDto?.url || data.organisationVideoDto.thumbnail,
      }] : [],
      languages: data.organisationLanguages?.map((lang: any) => ({
        code: lang.code,
        name: lang.name,
        isDefault: lang.primaryLanguage,
      })) || [],
      details: {
        vision: data.visionStatement || undefined,
        mission: data.missionStatement || undefined,
        executiveSummary: data.executiveSummary || undefined,
        history: data.history || undefined,
        organisationValues: data.organisationValues || undefined,
        programAndActivities: data.programAndActivities || undefined,
        yearFounded: data.yearFounded || undefined,
        website: data.organisationWebsite || undefined,
        categories: data.organisationCategoriesNames || undefined,
        influencedCountries: data.organisationInfluencedCountriesNames || undefined,
        globalInfluence: data.globalInfluence ?? false,
        socials: {
          facebook: data.organisationSocialMediaFacebook || undefined,
          twitter: data.organisationSocialMediaTwitter || undefined,
          instagram: data.organisationSocialMediaInstagram || undefined,
          linkedin: data.organisationSocialMediaLinkedin || undefined,
        },
        socialMedia: {
          facebook: data.organisationSocialMediaFacebook || undefined,
          twitter: data.organisationSocialMediaTwitter || undefined,
          instagram: data.organisationSocialMediaInstagram || undefined,
          linkedin: data.organisationSocialMediaLinkedin || undefined,
        },
      },
      team: this.mapTeamMembers(data),
      partnerOrganisations: (data.partnerOrganisations || []).map((p: any) => ({
        uuid: p.uuid,
        position: p.position,
        name: p.name || '',
        relationshipDescription: p.relationshipDescription || '',
        email: p.email || '',
      })),
      projects: data.projectPreviewDetails || [],
      financials: data.budgetOrganisations || [],
      financialReports: this.mapFinancialReports(data.budgetOrganisations || []),
      documents: data.organisationDocuments?.map((doc: any) => {
        const documentDto = doc.documentDto || doc;
        const filename = documentDto.filename || '';
        // Extract just the filename from the path (e.g., "path/to/file.pdf" -> "file.pdf")
        const displayName = filename.split('/').pop() || filename;

        return {
          id: documentDto.uuid,
          title: displayName || 'Document',
          type: documentDto.type,
          url: documentDto.url,
          uploadDate: doc.uploadDate || '',
          size: documentDto.size,
        };
      }) || [],
      champions: data.champions || [],
      championed: data.championed,
      givingLink: data.organisationGivingLink || undefined,
      isWatchlisted: data.addedToWatchlist || data.isWatchlisted || false, // Map the watchlist status from API (check both field names)
      organisationCauseName: data.organisationCauseName || undefined,
      createdAt: undefined,
      updatedAt: undefined,
    };

    return {
      success: true,
      data: charity,
      message: 'Charity retrieved successfully',
    };
  }

  /**
   * Maps team members from API response
   * @param data - The API response data
   * @returns Array of team members
   */
  private mapTeamMembers(data: any): any[] {
    const teamMembers: any[] = [];

    // Map Organisation Leader
    if (data.organisationLeader) {
      teamMembers.push({
        id: data.organisationLeader.uuid,
        name: data.organisationLeader.fullName || '',
        distinction: 'Organization Leader',
        role: data.organisationLeader.jobTitle || data.organisationLeader.role || '',
        bio: data.organisationLeader.bio,
        image: data.organisationLeader.profilePicture ? {
          id: data.organisationLeader.profilePicture.uuid,
          url: data.organisationLeader.profilePicture.url,
          alt: data.organisationLeader.fullName || '',
        } : undefined,
        email: data.organisationLeader.email,
        linkedin: data.organisationLeader.linkedinProfile,
      });
    }

    // Map Executive Teams
    if (data.executiveTeams && Array.isArray(data.executiveTeams)) {
      data.executiveTeams.forEach((member: any) => {
        teamMembers.push({
          id: member.uuid,
          name: member.fullName || '',
          distinction: 'Executive Team',
          role: member.jobTitle || member.role || '',
          bio: member.bio,
          image: member.profilePicture ? {
            id: member.profilePicture.uuid,
            url: member.profilePicture.url,
            alt: member.fullName || '',
          } : undefined,
          email: member.email,
          linkedin: member.linkedinProfile,
        });
      });
    }

    // Map Board Members
    if (data.boardMembers && Array.isArray(data.boardMembers)) {
      data.boardMembers.forEach((member: any) => {
        teamMembers.push({
          id: member.uuid,
          name: member.fullName || '',
          distinction: 'Board Member',
          role: member.jobTitle || member.role || '',
          bio: member.bio,
          image: member.profilePicture ? {
            id: member.profilePicture.uuid,
            url: member.profilePicture.url,
            alt: member.fullName || '',
          } : undefined,
          email: member.email,
          linkedin: member.linkedinProfile,
        });
      });
    }

    return teamMembers;
  }

  /**
   * Maps budget organisations (financial data) from API response to FinancialReport format
   * @param budgetOrganisations - The budget organisations array from API
   * @returns Array of financial reports
   */
  private mapFinancialReports(budgetOrganisations: any[]): any[] {
    if (!budgetOrganisations || !Array.isArray(budgetOrganisations)) {
      return [];
    }

    return budgetOrganisations
      .map((budget: any) => {
        // Extract revenue items (sorted by position)
        const revenueItems = budget.budgetRevenues?.map((rev: any) => ({
          description: rev.itemName || rev.description || '',
          actual: rev.actuals || rev.actualAmount || 0
        })).sort((a: any, b: any) => (a.position || 0) - (b.position || 0)) || [];

        // Extract expense items (sorted by position)
        const expenseItems = budget.budgetExpenses?.map((exp: any) => ({
          description: exp.itemName || exp.description || '',
          actual: exp.actuals || exp.actualAmount || 0
        })).sort((a: any, b: any) => (a.position || 0) - (b.position || 0)) || [];

        // Use backend-calculated totals instead of calculating from items
        const revenueTotal = budget.totalActualsRevenues || 0;
        const expenseTotal = budget.totalActualsExpenses || 0;

        // Use backend-calculated net gain/loss
        const netAmount = budget.netGainLossActuals || 0;
        const netType = netAmount >= 0 ? 'gain' : 'loss';

        // Extract year - budgetYear is an object with a 'name' property containing the year as a string
        let year = 0;
        if (typeof budget.budgetYear === 'number') {
          year = budget.budgetYear;
        } else if (budget.budgetYear && typeof budget.budgetYear === 'object') {
          // budgetYear is an object like { uuid: "...", name: "2024" }
          if (budget.budgetYear.name) {
            year = parseInt(budget.budgetYear.name, 10) || 0;
          } else if (budget.budgetYear.year) {
            year = budget.budgetYear.year;
          }
        } else if (budget.year) {
          year = budget.year;
        }

        // Extract currency - budgetCurrency is an object with a 'name' property
        let currency = 'USD';
        if (typeof budget.budgetCurrency === 'string') {
          currency = budget.budgetCurrency;
        } else if (budget.budgetCurrency && typeof budget.budgetCurrency === 'object' && budget.budgetCurrency.name) {
          currency = budget.budgetCurrency.name;
        } else if (budget.currency) {
          currency = budget.currency;
        }

        return {
          year: year,
          currency: currency,
          position: budget.position || 0,
          revenue: {
            items: revenueItems,
            total: revenueTotal
          },
          expense: {
            items: expenseItems,
            total: expenseTotal
          },
          netGainLoss: {
            totalRevenue: revenueTotal,  // Use totalActualsRevenues from backend
            totalExpense: expenseTotal,  // Use totalActualsExpenses from backend
            netAmount: Math.abs(netAmount),  // Use netGainLossActuals from backend (absolute value)
            type: netType
          }
        };
      })
      .sort((a, b) => a.position - b.position); // Sort by position (1, 2, 3...)
  }
}
